import Seo from "../components/Seo";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import LevelIndicator from "../components/LevelIndicator";
import TierBadge from "../components/TierBadge";
import { Crumbs, RetroDate, SignupHint, levelRange } from "../components/CourseBits";
import { useCourseProgress, useCoursesData } from "../lib/courseData";
import { relatedOf, sortRetros, themeIcon } from "../lib/courseMeta";
function ProgressCard({ theme, readIds, loggedIn }) {
  const list = theme.courses, n = list.length;
  const done = list.filter((c) => readIds.has(c.id)).length;
  const next = list.find((c) => !readIds.has(c.id));
  const minutes = list.reduce((a, c) => a + (c.reading_minutes || 0), 0);
  // Téléphone : bloc compact (bouton plus petit, à côté de la progression) ; ordinateur inchangé
  const cta = (to, label, mobile = "") => (
    <Link to={to} className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md h-11 px-4 text-sm font-medium bg-emerald-500 text-black hover:bg-emerald-300 ${mobile}`} data-testid="theme-cta">
      {label} <ArrowRight className="w-4 h-4" />
    </Link>
  );
  if (!n) return null;
  if (!loggedIn)
    return (
      <div className="sm:rounded-xl sm:border sm:border-white/10 sm:bg-card sm:p-5">
        <div className="hidden sm:block text-sm text-zinc-400">{n} cours · {minutes} min de lecture</div>
        {cta(`/cours/${list[0].slug}`, "Commencer le parcours", "max-sm:mt-0 max-sm:w-auto max-sm:h-10")}
        <div className="mt-3 sm:mt-4"><SignupHint className="text-xs" /></div>
      </div>
    );
  if (done === n)
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-4 sm:p-5 max-sm:flex max-sm:items-center max-sm:gap-3">
        <div className="flex items-center gap-2 text-emerald-300 font-medium max-sm:flex-1 max-sm:text-sm"><Check className="w-5 h-5" /> Thématique terminée</div>
        <p className="hidden sm:block mt-1 text-sm text-zinc-400">Vous avez lu les {n} cours de ce parcours.</p>
        <Link to={`/cours/${list[0].slug}`} className="mt-4 inline-flex w-full items-center justify-center rounded-md h-11 px-4 text-sm border border-white/15 bg-white/[0.04] hover:bg-white/10 max-sm:mt-0 max-sm:w-auto max-sm:h-9 max-sm:shrink-0">
          Revoir depuis le début
        </Link>
      </div>
    );
  return (
    <div className="rounded-xl border border-white/10 bg-card p-4 sm:p-5" data-testid="theme-progress">
      <div className="max-sm:flex max-sm:items-center max-sm:gap-4">
        <div className="max-sm:flex-1 max-sm:min-w-0">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-zinc-400">Votre progression</span>
            <span className="font-display font-bold text-xl">{done}<span className="text-zinc-500 text-base"> / {n}</span></span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-orange-400 rounded-full" style={{ width: `${(done / n) * 100}%` }} />
          </div>
        </div>
        {cta(`/cours/${next.slug}`, done ? "Continuer" : "Commencer", "max-sm:mt-0 max-sm:w-auto max-sm:h-9 max-sm:shrink-0")}
      </div>
      <p className="hidden sm:block mt-2 text-xs text-zinc-500 truncate">{done ? "Prochain cours" : "Premier cours"} : {next.title}</p>
    </div>
  );
}
/** Page d'une thématique : le parcours dans l'ordre choisi par l'admin, la progression et les rétrospectives liées. */
export default function CourseThemePage() {
  const { slug } = useParams();
  const { courses, themes, isLoading } = useCoursesData();
  const { loggedIn, readIds } = useCourseProgress();
  const theme = themes.find((t) => t.slug === slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (isLoading) {
    return <div className="mx-auto max-w-6xl px-6 py-16"><div className="h-10 w-1/2 bg-white/5 rounded animate-pulse" /></div>;
  }
  if (!theme) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display font-black text-3xl mb-3">Thématique introuvable</h1>
        <p className="text-zinc-500 mb-6">Cette thématique n'existe pas ou n'est plus disponible.</p>
        <Button asChild variant="outline" className="border-white/10 bg-transparent">
          <Link to="/cours"><ArrowLeft className="w-4 h-4 mr-1.5" /> Retour aux cours</Link>
        </Button>
      </div>
    );
  }
  const Icon = themeIcon(theme.icon);
  const list = theme.courses;
  const nextId = loggedIn ? list.find((c) => !readIds.has(c.id))?.id : null;
  const minutes = list.reduce((a, c) => a + (c.reading_minutes || 0), 0);
  const retros = sortRetros(
    [...new Set(list.flatMap((c) => relatedOf(c, courses)).filter((x) => x.category === "article"))]
  );
  const others = themes.filter((t) => t.id !== theme.id);
  return (
    <div className="min-h-screen" data-testid="course-theme-page">
      <Seo title={theme.title} description={theme.description || undefined} path={`/cours/thematique/${theme.slug}`} />
      <section className="border-b border-white/5">
        <div className="mx-auto max-w-6xl px-6 pt-8 pb-6 sm:pb-10">
          <Crumbs items={[["Cours", "/cours"], ["Concepts", "/cours"], [theme.title]]} />
          <div className="mt-6 grid gap-6 sm:gap-8 lg:grid-cols-[1fr_340px] lg:items-end">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 grid place-items-center rounded-lg bg-orange-500/10 border border-orange-500/25 text-orange-300"><Icon className="w-5 h-5" /></div>
                <span className="text-xs uppercase tracking-[0.2em] text-orange-400">Thématique</span>
              </div>
              <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl leading-tight">{theme.title}</h1>
              {theme.description && <p className="mt-3 text-zinc-400 max-w-xl">{theme.description}</p>}
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-500">
                <span>{list.length} cours</span>
                {minutes > 0 && <span>{minutes} min de lecture</span>}
                {levelRange(list) && <span>{levelRange(list)}</span>}
              </div>
            </div>
            <ProgressCard theme={theme} readIds={readIds} loggedIn={loggedIn} />
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-6 pt-7 pb-10 sm:py-10 grid gap-10 lg:grid-cols-[1fr_300px]">
        <div>
          <h2 className="font-display font-black text-2xl">Le parcours</h2>
          <p className="text-sm text-zinc-400 mt-1 mb-6">Dans l'ordre de lecture conseillé.</p>
          {list.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 py-12 text-center text-sm text-zinc-500">Les cours de cette thématique arrivent bientôt.</div>
          ) : (
            <ol className="relative" data-testid="theme-course-list">
              {list.map((c, k) => {
                const isRead = loggedIn && readIds.has(c.id), isNext = c.id === nextId, last = k === list.length - 1;
                return (
                  <li key={c.id} className="relative pl-12 pb-3">
                    {!last && <span className={`absolute left-[15px] top-9 bottom-0 w-px ${isRead ? "bg-emerald-500/30" : "bg-white/10"}`} />}
                    <span className="absolute left-0 top-3">
                      {isRead ? (
                        <span className="w-8 h-8 grid place-items-center rounded-full bg-emerald-500/15 border border-emerald-500/50 text-emerald-300"><Check className="w-4 h-4" /></span>
                      ) : (
                        <span className={`w-8 h-8 grid place-items-center rounded-full border text-sm font-semibold ${isNext ? "border-orange-400 text-orange-300 bg-orange-500/10 ring-4 ring-orange-500/10" : "border-white/15 text-zinc-400 bg-[#0a0a0a]"}`}>{k + 1}</span>
                      )}
                    </span>
                    <Link
                      to={`/cours/${c.slug}`}
                      className={`group block rounded-xl border ${isNext ? "border-orange-500/30 bg-orange-500/[0.03]" : "border-white/5 bg-card"} hover:border-orange-500/40 transition-colors px-5 py-4`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          {isNext && <div className="text-[10px] uppercase tracking-[0.18em] text-orange-400 mb-1">À lire ensuite</div>}
                          <h3 className={`font-display font-bold leading-snug group-hover:text-orange-300 transition-colors ${isRead ? "text-zinc-300" : ""}`}>{c.title}</h3>
                          {c.description && <p className="mt-1 text-sm text-zinc-400">{c.description}</p>}
                          {/* Téléphone : le badge d'offre passe à côté du niveau, pour laisser toute la largeur au texte */}
                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                            <LevelIndicator level={c.level} />
                            {c.is_premium && <span className="sm:hidden"><TierBadge tier="essentiel" locked={!!c.is_locked} /></span>}
                          </div>
                        </div>
                        <div className="shrink-0 flex flex-col items-end gap-1.5 text-xs text-zinc-500">
                          {c.reading_minutes ? <span>{c.reading_minutes} min</span> : null}
                          {c.is_premium && <span className="hidden sm:inline-flex"><TierBadge tier="essentiel" locked={!!c.is_locked} /></span>}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <aside className="space-y-8">
          {retros.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1">En pratique</div>
              <h3 className="font-display font-bold text-lg mb-3">Ces notions dans l'histoire</h3>
              <div className="space-y-2">
                {retros.map((e) => (
                  <Link key={e.id} to={`/cours/${e.slug}`} className="group flex items-center gap-3 rounded-lg border border-white/5 bg-card px-3 py-2.5 hover:border-orange-500/30">
                    <RetroDate course={e} className="text-sm text-orange-300/90 w-[4.5rem] shrink-0" />
                    <span className="flex-1 text-sm text-zinc-300 group-hover:text-white leading-snug">{e.title}</span>
                    {loggedIn && readIds.has(e.id) && <Check className="w-3.5 h-3.5 text-emerald-300" />}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {others.length > 0 && (
            <div>
              <h3 className="font-display font-bold text-lg mb-3">Autres thématiques</h3>
              <div className="rounded-xl border border-white/5 bg-card divide-y divide-white/5">
                {others.map((o) => {
                  const OIcon = themeIcon(o.icon);
                  const d = o.courses.filter((c) => readIds.has(c.id)).length, m = o.courses.length;
                  return (
                    <Link key={o.id} to={`/cours/thematique/${o.slug}`} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] group">
                      <OIcon className="w-4 h-4 text-orange-300/80 shrink-0" />
                      <span className="flex-1 text-sm text-zinc-300 group-hover:text-white">{o.title}</span>
                      <span className={`text-xs ${loggedIn && m && d === m ? "text-emerald-300" : "text-zinc-500"}`}>
                        {loggedIn ? (m && d === m ? "Terminé" : `${d}/${m}`) : `${m} cours`}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}
